/**
 * ReportSeriesCard - Individual Series Card
 *
 * Displays a single report series with its latest snapshot info.
 */

import React from 'react';
import {
  Pin,
  Clock,
  FileText,
  ChevronRight,
  Play
} from 'lucide-react';
import { useFormat } from '../../../i18n/format.js';
import { FREQUENCY_LABELS } from '../../../hooks/useReportSeries.js';

// Icon mapping
const ICON_MAP = {
  Car: () => <span>🚗</span>,
  IdCard: () => <span>🪪</span>,
  Users: () => <span>👥</span>,
  Shield: () => <span>🛡️</span>,
  Calculator: () => <span>🧮</span>,
  Fuel: () => <span>⛽</span>,
  TrendingUp: () => <span>📈</span>,
  AlertTriangle: () => <span>⚠️</span>,
  Clock: () => <span>⏰</span>,
  Wrench: () => <span>🔧</span>,
  LayoutDashboard: () => <span>📊</span>,
  FileText: () => <span>📄</span>
};

// Color mapping for backgrounds
const COLOR_BG = {
  blue: 'bg-blue-50 text-blue-700',
  purple: 'bg-purple-50 text-purple-700',
  amber: 'bg-amber-50 text-amber-700',
  red: 'bg-red-50 text-red-700',
  green: 'bg-green-50 text-green-700',
  indigo: 'bg-indigo-50 text-indigo-700',
  gray: 'bg-gray-50 text-gray-700'
};

const COLOR_BADGE = {
  blue: 'bg-blue-100 text-blue-800',
  purple: 'bg-purple-100 text-purple-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  green: 'bg-green-100 text-green-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  gray: 'bg-gray-100 text-gray-800'
};

export default function ReportSeriesCard({
  series,
  onSelect,
  onTogglePin,
  onRun,
  isPinned = false
}) {
  const { t, fmt } = useFormat();
  const {
    display_name,
    frequency,
    snapshot_count,
    last_run_at,
    template,
    latest_snapshot
  } = series;

  const color = template?.color || 'gray';
  const Icon = ICON_MAP[template?.icon] || ICON_MAP.FileText;

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return t('reports.series.never');
    return fmt.date(dateStr, 'medium');
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('reports.series.today');
    if (diffDays === 1) return t('reports.series.yesterday');
    if (diffDays < 7) return t('reports.series.daysAgo', { count: diffDays });
    if (diffDays < 30) return t('reports.series.weeksAgo', { count: Math.floor(diffDays / 7) });
    return formatDate(dateStr);
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer group"
      onClick={onSelect}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${COLOR_BG[color]}`}>
              <Icon />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                {display_name}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 text-xs rounded-full ${COLOR_BADGE[color]}`}>
                  {t(`reports.schedule.frequencies.${frequency}.label`, { defaultValue: FREQUENCY_LABELS[frequency] || frequency })}
                </span>
              </div>
            </div>
          </div>

          {/* Pin Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin?.();
            }}
            className={`p-1.5 rounded-lg transition-colors ${
              isPinned
                ? 'text-amber-500 bg-amber-50 hover:bg-amber-100'
                : 'text-gray-400 hover:text-amber-500 hover:bg-gray-100'
            }`}
            title={isPinned ? t('reports.series.unpin') : t('reports.series.pin')}
          >
            <Pin className={`w-4 h-4 ${isPinned ? 'fill-current' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="p-4 bg-gray-50/50">
        <div className="grid grid-cols-2 gap-4">
          {/* Snapshot Count */}
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">
              {t('reports.series.snapshotsCount', { count: snapshot_count || 0 })}
            </span>
          </div>

          {/* Last Run */}
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">
              {formatRelativeTime(last_run_at)}
            </span>
          </div>
        </div>

        {/* Latest Snapshot Info */}
        {latest_snapshot && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">{t('reports.series.latest')}:</span>
              <span className="font-medium text-gray-700">
                {formatDate(latest_snapshot.as_of)}
                {latest_snapshot.record_count && (
                  <span className="text-gray-400 ml-1">
                    ({t('reports.series.recordsShort', { count: latest_snapshot.record_count })})
                  </span>
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRun?.();
          }}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-green-600 transition-colors"
          title={t('reports.series.generateNow')}
        >
          <Play className="w-3.5 h-3.5" />
          {t('reports.annual.generate')}
        </button>

        <div className="flex items-center gap-1 text-blue-600 text-xs font-medium group-hover:translate-x-1 transition-transform">
          {t('reports.series.viewHistory')}
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}
